import { takeEvery, fork, put, call, select } from 'redux-saga/effects';
import { cloneDeep } from 'lodash-es';
import i18n from '@/../i18n';
import { ShowToast } from '@/store/toast/actions';
import {
    ActionTypes,
    BrandColorsAddStartAction,
    BrandColorsDeleteStartAction,
    BrandColorsEditStartAction,
} from '@/store/brandcolors/types';
import {
    loadBrandColors as loadBrandColorsApi,
    updateBrandColors as updateBrandColorsApi,
} from '@/services/ApiCategories/brandColors';
import { isSameHex } from '@/services/tools/isSameHex';
import {
    BrandColorsLoadSuccess,
    BrandColorsLoadError,
    getBrandColorsStore,
    BrandColorsAddError,
    BrandColorsAddSuccess,
    BrandColorsEditSuccess,
    BrandColorsEditError,
    BrandColorsDeleteSuccess,
    BrandColorsDeleteError,
} from '../store/brandcolors/actions';
import { errorToast } from './commonGenerators/errorToast';

function* loadBrandColors(): any {
    try {
        const list = yield call(loadBrandColorsApi);
        if (list.status !== 200) {
            throw new Error(i18n.t('apitimeout'));
        }
        yield put(BrandColorsLoadSuccess(list.data));
    } catch (e) {
        yield put(BrandColorsLoadError());
        yield call(errorToast, e);
    }
}

function* addBrandColor(action: BrandColorsAddStartAction): any {
    try {
        const { list } = yield select(getBrandColorsStore);
        if (list?.some((color) => isSameHex(color.Hex, action.payload.color.Hex))) {
            yield put(
                ShowToast({
                    message: i18n.t('brandcoloralreadyexists'),
                    severity: 'info',
                    duration: 6000,
                })
            );
            return;
        }
        const newColors = [...(list ?? []), action.payload.color];
        const response = yield call(updateBrandColorsApi, newColors, list);
        if (response.status !== 200) {
            throw new Error(i18n.t('apitimeout'));
        }
        yield put(BrandColorsAddSuccess(response.data));
        yield put(
            ShowToast({
                message: i18n.t('brandcoloradded'),
                severity: 'info',
                duration: 6000,
            })
        );
    } catch (e) {
        yield put(BrandColorsAddError(action.payload.color));
        yield call(errorToast, e);
    }
}

function* editBrandColor(action: BrandColorsEditStartAction): any {
    try {
        const { list } = yield select(getBrandColorsStore);
        const newColors = cloneDeep(list);
        const currentColor = newColors[action.payload.colorIndex];
        const isNameChanged = currentColor.Name !== action.payload.color.Name;
        const isHexChanged = currentColor.Hex !== action.payload.color.Hex;
        if (list?.some((color) => isSameHex(color.Hex, action.payload.color.Hex)) && isHexChanged) {
            yield put(
                ShowToast({
                    message: i18n.t('brandcoloralreadyexists'),
                    severity: 'info',
                    duration: 6000,
                })
            );
            return;
        }
        newColors[action.payload.colorIndex] = action.payload.color;
        const response = yield call(updateBrandColorsApi, newColors, list);
        if (response.status !== 200) {
            throw new Error(i18n.t('apitimeout'));
        }
        yield put(BrandColorsEditSuccess(response.data));

        if (isNameChanged && !isHexChanged) {
            yield put(
                ShowToast({
                    message: i18n.t('brandcolornameedited'),
                    severity: 'info',
                    duration: 6000,
                })
            );
        }
    } catch (e) {
        yield put(BrandColorsEditError(action.payload.color));
        yield call(errorToast, e);
    }
}

function* deleteBrandColor(action: BrandColorsDeleteStartAction): any {
    try {
        const { list } = yield select(getBrandColorsStore);
        const newColors = cloneDeep(list);
        newColors.splice(action.payload.colorIndex, 1);
        const response = yield call(updateBrandColorsApi, newColors, list);
        if (response.status !== 200) {
            throw new Error(i18n.t('apitimeout'));
        }
        yield put(BrandColorsDeleteSuccess(response.data));
        yield put(
            ShowToast({
                message: i18n.t('brandcolordeleted'),
                severity: 'info',
                duration: 6000,
            })
        );
    } catch (e) {
        yield put(BrandColorsDeleteError(action.payload.color));
        yield call(errorToast, e);
    }
}

function* loadBrandColorsWatcher(): any {
    yield takeEvery(ActionTypes.LOAD_START, loadBrandColors);
}

function* addBrandColorWatcher(): any {
    yield takeEvery(ActionTypes.ADD_START, addBrandColor);
}

function* editBrandColorWatcher(): any {
    yield takeEvery(ActionTypes.EDIT_START, editBrandColor);
}

function* deleteBrandColorWatcher(): any {
    yield takeEvery(ActionTypes.DELETE_START, deleteBrandColor);
}

const brandColorsSagas = [
    fork(loadBrandColorsWatcher),
    fork(addBrandColorWatcher),
    fork(deleteBrandColorWatcher),
    fork(editBrandColorWatcher),
];

export default brandColorsSagas;
